import PropTypes from "prop-types"
import React, { useCallback, useEffect, useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import { SERVICE_TEXT_MAX_LENGTH } from "src/main/Service/Manage/shared/textLimits"

import Button from "src/components/Button"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Form from "src/components/Form"
import Modal from "src/components/Modal"

import { updateWorkOrderRequestDescription } from "src/api/Service/WorkOrders"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { EDIT_REQUEST_DESCRIPTION_DISCARDED } from "./amplitude_events"

const EditRequestDescriptionModal = ({
  isOpen,
  onClose,
  requestDescription,
  workOrderSequentialId,
}) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: { requestDescription: requestDescription ?? "" },
  })

  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      reset({ requestDescription: requestDescription ?? "" })
    }
    wasOpenRef.current = isOpen
  }, [isOpen, requestDescription, reset])

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty,
    onClose,
  })

  const handleDiscard = useCallback(() => {
    tracker.trackEvent(EDIT_REQUEST_DESCRIPTION_DISCARDED, {
      work_order_id: workOrderSequentialId,
    })
    discardConfirmProps.onDiscard()
  }, [tracker, workOrderSequentialId, discardConfirmProps])

  const invalidateWorkOrder = () =>
    queryClient.invalidateQueries([
      "workOrder",
      marinaSlug,
      workOrderSequentialId,
    ])

  const { mutate: submit, isLoading } = useMutation(
    (values) =>
      updateWorkOrderRequestDescription(
        marinaSlug,
        workOrderSequentialId,
        values
      ),
    {
      onSuccess: () => {
        invalidateWorkOrder()
        showToast("Request description updated", { type: "success" })
        onClose()
      },
      onError: (error) => {
        invalidateWorkOrder()
        const message = Array.isArray(error?.message)
          ? error.message.join(". ")
          : error?.message
        showToast(message || "Failed to update request description.", {
          type: "error",
        })
      },
    }
  )

  const onSubmit = (values) =>
    submit({ requestDescription: values.requestDescription })

  const title = requestDescription?.trim()
    ? "Edit Request Description"
    : "Add Request Description"

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseRequest}
        dismissable={!isLoading}
        size="default"
      >
        <Modal.Header title={title} />
        <Modal.Body>
          <Form.Label htmlFor="edit-request-description">
            Request Description
          </Form.Label>
          <Controller
            name="requestDescription"
            control={control}
            render={({ field }) => (
              <Form.Textarea
                id="edit-request-description"
                rows={4}
                maxLength={SERVICE_TEXT_MAX_LENGTH}
                autoFocus
                placeholder="What did the customer request?"
                {...field}
              />
            )}
          />
        </Modal.Body>

        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={handleCloseRequest}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
              disabled={!isDirty || isLoading}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <DiscardConfirmModal {...discardConfirmProps} onDiscard={handleDiscard} />
    </>
  )
}

EditRequestDescriptionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestDescription: PropTypes.string,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default EditRequestDescriptionModal
